import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ErrorDeApi } from '@nucleo/api/cliente'
import { Boton } from '@ui/primitivas/Boton'
import { listarMatrices } from './clienteDeMatrices'
import { ETAPAS_DE_LA_MATRIZ } from './etapas'
import type { ResumenDeMatriz } from './tipos'

type Filtro = 'todas' | 'borrador' | 'oficial'

function GuiaDeEtapas() {
  return (
    <section aria-labelledby="como-avanza" className="sst-tarjeta p-5">
      <h2 id="como-avanza" className="text-h3">Cómo avanza una matriz</h2>
      <p className="mt-1 text-pequeno text-texto-secundario">Cuatro etapas. En cada una Atalaya propone y tú validas; nada queda oficial sin tu confirmación.</p>
      <ol className="sst-etapas mt-4" data-orientacion="horizontal">
        {ETAPAS_DE_LA_MATRIZ.map((etapa) => (
          <li key={etapa.ruta} className="sst-etapa" style={{ cursor: 'default' }}>
            <span className="sst-etapa-numero" aria-hidden="true">{etapa.numero}</span>
            <span className="sst-etapa-textos"><span>{etapa.titulo}</span><small>{etapa.detalle}</small></span>
          </li>
        ))}
      </ol>
    </section>
  )
}

export function ListaDeMatrices(props: { alSesionInvalida: () => void }) {
  const { alSesionInvalida } = props
  const [matrices, setMatrices] = useState<ResumenDeMatriz[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<Filtro>('todas')
  const [cargando, setCargando] = useState(true)

  const cargar = useCallback(() => {
    let vigente = true
    setCargando(true)
    setError(null)
    listarMatrices()
      .then((m) => { if (vigente) setMatrices(m) })
      .catch((e: unknown) => {
        if (e instanceof ErrorDeApi && e.estado === 401) alSesionInvalida()
        else if (vigente) setError('No fue posible cargar tus matrices. Revisa tu conexión y vuelve a intentarlo.')
      })
      .finally(() => { if (vigente) setCargando(false) })
    return () => { vigente = false }
  }, [alSesionInvalida])

  useEffect(() => cargar(), [cargar])

  const cuenta = (f: Filtro) => (matrices ?? []).filter((m) => f === 'todas' || m.estado === f).length
  const visibles = (matrices ?? []).filter((m) => filtro === 'todas' || m.estado === filtro)

  return (
    <section className="grid gap-5" aria-labelledby="titulo-matrices">
      <header className="sst-hero">
        <div>
          <p className="cs-eyebrow">Matriz IPER asistida</p>
          <h1 id="titulo-matrices">Matrices de la organización</h1>
          <p>Retoma un borrador donde quedó o descarga la revisión oficial de una MIPER. Cada matriz cubre un centro de trabajo y su alcance.</p>
        </div>
        <div className="sst-hero-acciones">
          <Link to="/matrices/nueva" className="sst-cta sst-cta--claro">
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            Nueva matriz
          </Link>
        </div>
      </header>

      {error ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-critico/40 bg-critico-fondo p-4 text-critico-texto">
          <span>{error}</span>
          <Boton variante="secundario" compacto alPulsar={cargar}>Reintentar</Boton>
        </div>
      ) : null}

      {cargando && matrices === null && !error ? (
        <ul className="grid gap-3 md:grid-cols-2" aria-busy="true" aria-label="Cargando matrices">
          {[0, 1].map((i) => <li key={i} className="sst-tarjeta h-28 opacity-60" />)}
        </ul>
      ) : null}

      {matrices?.length === 0 ? (
        <div className="sst-vacio">
          <span className="cs-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16v14H4zM4 10h16M10 10v9" /></svg>
          </span>
          <h2>Todavía no hay matrices</h2>
          <p>Crea la primera: registra el alcance del centro de trabajo, describe el proceso y Atalaya propondrá tareas, peligros, evaluación y medidas para que las valides.</p>
          <Link to="/matrices/nueva" className="sst-cta">Crear la primera matriz</Link>
        </div>
      ) : null}

      {matrices && matrices.length > 0 ? (
        <section className="grid gap-3" aria-labelledby="titulo-listado">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="titulo-listado" className="text-h2">Tus matrices</h2>
            <div className="flex flex-wrap items-center gap-2">
              <div className="sst-filtro" role="group" aria-label="Filtrar por estado">
                {([['todas', 'Todas'], ['borrador', 'Borradores'], ['oficial', 'Oficiales']] as const).map(([valor, etiqueta]) => (
                  <button key={valor} type="button" aria-pressed={filtro === valor} onClick={() => setFiltro(valor)}>
                    {etiqueta}<b>{cuenta(valor)}</b>
                  </button>
                ))}
              </div>
              <Boton variante="secundario" compacto trabajando={cargando} alPulsar={cargar}>Actualizar</Boton>
            </div>
          </div>
          {visibles.length === 0 ? <p className="text-pequeno text-texto-secundario">No hay matrices con ese estado.</p> : null}
          <ul className="grid gap-3 md:grid-cols-2">
            {visibles.map((m) => (
              <li key={m.id}>
                <Link to={`/matrices/${m.id}`} className="sst-tarjeta sst-tarjeta-interactiva grid h-full gap-2 p-4 no-underline">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <span className="text-h3 text-texto">{m.alcance}</span>
                    <span className={`cs-state ${m.estado === 'oficial' ? 'cs-state--live' : 'cs-state--soon'}`}>
                      {m.estado === 'oficial' ? `Oficial · rev. ${m.revision_actual}` : m.revision_actual > 0 ? `Borrador rev. ${m.revision_actual + 1}` : 'Borrador'}
                    </span>
                  </div>
                  <span className="text-pequeno text-texto-secundario">{m.empresa}</span>
                  <span className="text-pequeno text-texto-secundario">{m.centro}</span>
                  <span className="mt-1 flex flex-wrap items-center justify-between gap-2 text-pequeno">
                    <span className="text-texto-secundario">Actualizada {new Date(m.actualizada_en).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    <span className="font-semibold text-accion">{m.estado === 'oficial' ? 'Ver revisión' : 'Continuar'} →</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {matrices !== null && matrices.length < 3 ? <GuiaDeEtapas /> : null}
    </section>
  )
}
